/**
 * @fileoverview 后端 API 请求封装
 * @description 统一管理所有后端 HTTP 请求，包含 Token 管理、错误处理和响应解析
 * MVC 架构：Service 层，负责与后端通信，不包含业务逻辑
 * @module services/api
 */

// ============================================================
// 常量配置
// ============================================================

/** 后端 API 基础地址，从环境变量读取，默认为生产服务器地址 */
// 使用相对路径，由 Vite 代理转发到后端服务器（解决 HTTPS 混合内容问题）
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/** localStorage 中存储 Token 的键名 */
const TOKEN_KEY = 'pythia_auth_token';

/** 后端响应成功状态码 */
export const API_OK = 10000;

// ============================================================
// 错误码枚举（与后端 error.js 保持一致）
// ============================================================

// ============================================================
// Token 过期全局事件
// ============================================================

/**
 * 触发 token 过期全局事件（防抖：500ms 内只触发一次）
 * 由顶层组件监听后执行 logout + toast + 跳转
 */
let _tokenExpiredTimer: ReturnType<typeof setTimeout> | null = null;
export function emitTokenExpired(): void {
  if (_tokenExpiredTimer) return;
  _tokenExpiredTimer = setTimeout(() => {
    _tokenExpiredTimer = null;
  }, 500);
  window.dispatchEvent(new CustomEvent('token-expired'));
}

/** 后端错误码映射 */
export const ApiErrorCode = {
  OK: 10000,
  errException: 10001,
  errTokenExpired: 10002,
  errInvalidAuther: 10003,
  errInvalidParam: 10005,
  errInvalidLoginData: 20001,
  errInvalidSignature: 20010,
  errInvalidToken: 20011,
  errInvalidMarket: 30002,
  errInsufficientBalance: 10022,
  /** 押注金额低于市场设置的最小单笔金额 */
  errLowInvestAmount: 20015,
  /** 押注金额高于市场设置的最大单笔金额 */
  errHighInvestAmount: 20016,
} as const;

// ============================================================
// Token 管理工具
// ============================================================

/**
 * 保存认证 Token 到 localStorage
 * @param token - JWT Token（格式：`"Bearer <jwt>"`）
 */
export function saveToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * 从 localStorage 读取认证 Token
 * @returns Token 字符串，未登录时返回 null
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * 清除 localStorage 中的认证 Token
 */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// ============================================================
// 通用请求函数
// ============================================================

/**
 * 后端 API 响应结构
 */
export interface BackendResponse<T = unknown> {
  status: number;
  data?: T;
  msg?: string;
}

/**
 * 发起 HTTP 请求的通用函数
 * @param path - API 路径（如 `/api/v1/evmLogin`）
 * @param options - fetch 选项
 * @param requireAuth - 是否需要携带认证 Token，默认 false
 * @returns 解析后的响应数据
 * @throws 当请求失败或后端返回错误状态码时抛出错误
 */
async function request<T>(
  path: string,
  options: RequestInit = {},
  requireAuth = false
): Promise<BackendResponse<T>> {
  const url = `${API_BASE_URL}${path}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  // 需要认证时，自动附加 Token
  if (requireAuth) {
    const token = getToken();
    if (token) {
      // 后端 verifyLoginToken 从 Authorization header 解析 "Bearer <jwt>"
      headers['Authorization'] = token;
    }
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const json: BackendResponse<T> = await response.json();

  // 统一拦截 token 过期 / 无效 token 错误码
  if (
    json.status === ApiErrorCode.errTokenExpired ||
    json.status === ApiErrorCode.errInvalidAuther ||
    json.status === ApiErrorCode.errInvalidToken
  ) {
    emitTokenExpired();
  }

  return json;
}

/**
 * GET 请求封装
 * @param path - API 路径
 * @param params - URL 查询参数
 * @param requireAuth - 是否需要认证
 */
export async function get<T>(
  path: string,
  params?: Record<string, string | number>,
  requireAuth = false
): Promise<BackendResponse<T>> {
  let url = path;
  if (params && Object.keys(params).length > 0) {
    const query = new URLSearchParams(
      Object.entries(params).map(([k, v]) => [k, String(v)])
    ).toString();
    url = `${path}?${query}`;
  }
  return request<T>(url, { method: 'GET' }, requireAuth);
}

/**
 * POST 请求封装
 * @param path - API 路径
 * @param body - 请求体数据
 * @param requireAuth - 是否需要认证
 */
export async function post<T>(
  path: string,
  body?: unknown,
  requireAuth = false
): Promise<BackendResponse<T>> {
  return request<T>(
    path,
    {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    },
    requireAuth
  );
}
